import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, createElement, useContext, useMemo } from "react";
import { DEFAULT_THEME, THEME_TOKENS } from "./tokens.js";
const ThemeContext = createContext({
    primaryColor: DEFAULT_THEME.primaryColor,
    secondaryColor: DEFAULT_THEME.secondaryColor,
    accentColor: DEFAULT_THEME.accentColor,
});
/**
 * Applies a tenant's branding colors as CSS custom properties on a wrapper element, so
 * every descendant component can pick them up via `var(--sl-color-primary, ...)` without
 * a rebuild (system_architecture.md §11.1: "One build, dynamic theme"). Also exposes the
 * resolved values via `useTheme()` for components/apps that need the raw color string
 * (e.g. passing a hex to a canvas/chart library that doesn't read CSS variables).
 *
 * This component only *consumes* a branding shape — fetching `TenantBranding` from the
 * backend (once that route exists, see the type's doc comment) is the responsibility of
 * whichever `apps/*` package renders this provider.
 */
export function ThemeProvider({ branding, children, as = "div", className, style }) {
    const resolved = useMemo(() => ({
        primaryColor: branding?.primary_color ?? DEFAULT_THEME.primaryColor,
        secondaryColor: branding?.secondary_color ?? DEFAULT_THEME.secondaryColor,
        accentColor: branding?.accent_color ?? DEFAULT_THEME.accentColor,
    }), [branding?.primary_color, branding?.secondary_color, branding?.accent_color]);
    // Stamp ONLY the colors the tenant actually set. Stamping DEFAULT_THEME fallbacks here
    // would override an app's own `:root` token (e.g. dashboard-admin's ledger-green
    // `--sl-color-secondary` realm chrome) with the generic default for every tenant that
    // hasn't configured branding — the CSS variable cascade (app :root → styles.css
    // defaults) already provides the fallback chain, so an unset field needs no inline var.
    const cssVars = useMemo(() => {
        const vars = {};
        if (branding?.primary_color)
            vars[THEME_TOKENS.colorPrimary] = branding.primary_color;
        if (branding?.secondary_color)
            vars[THEME_TOKENS.colorSecondary] = branding.secondary_color;
        if (branding?.accent_color)
            vars[THEME_TOKENS.colorAccent] = branding.accent_color;
        return vars;
    }, [branding?.primary_color, branding?.secondary_color, branding?.accent_color]);
    return (_jsx(ThemeContext.Provider, { value: resolved, children: createElement(as, { className, style: { ...cssVars, ...style } }, children) }));
}
/** Read the currently resolved theme colors (branding-supplied, or `DEFAULT_THEME` if no
 * `ThemeProvider` is mounted above the caller). */
export function useTheme() {
    return useContext(ThemeContext);
}
//# sourceMappingURL=ThemeProvider.js.map